// src/admin/NavAdmin.jsx
import React, { useEffect, useState } from "react";
import { ref, get, set } from "firebase/database";
import { db } from "../../lib/firebase";
import "./NavAdmin.css";
import { CONFIG } from "../../config";

/** ---- Config ---- */
const UPLOAD_URL = CONFIG.UPLOAD_URL;
const IMAGES_BASE = CONFIG.IMAGES_BASE;

const emptyDropdown = () => ({ name: "", items: [{ id: 1, label: "" }] });
const emptyPage = () => ({ label: "", href: "", show: true, type: "page" }); // Added type field
const emptyHomepageSection = () => ({ 
  label: "", 
  sectionId: "", 
  show: true, 
  type: "homepage" 
});

// NEW: Homepage sections available for linking
const HOMEPAGE_SECTIONS = [
  { id: "hero", label: "Hero Section" },
  { id: "aboutUs", label: "About Us" },
  { id: "howWeWork", label: "How We Work" },
  { id: "services", label: "Our Services" },
  { id: "customerReviews", label: "Customer Reviews" },
  { id: "specialCategory", label: "Special Categories" },
  { id: "categories", label: "Categories" },
  { id: "latest", label: "Latest Projects" },
  { id: "updates", label: "Updates & News" },
  { id: "instantQuote", label: "Instant Quote Form" },
  { id: "documents", label: "PDF Documents" },
];

function isNonEmptyString(v) {
  return typeof v === "string" && v.trim() !== "";
}
function toImageSrc(val) {
  if (!isNonEmptyString(val)) return "";
  if (/^https?:\/\//i.test(val) || /^\/\//.test(val)) return val;
  if (val.startsWith("/")) return val;
  return `${IMAGES_BASE}/${encodeURIComponent(val)}`;
}
function slug(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}
function isExternalLike(href) {
  const value = String(href || "");
  return (
    /^https?:\/\//i.test(value) ||
    value.startsWith("//") ||
    /^[a-z][a-z0-9+.-]*:/i.test(value)
  );
}
function normalizePageHref(href) {
  if (!isNonEmptyString(href)) return "";
  const trimmed = String(href).trim();
  if (trimmed.startsWith("#")) return trimmed;
  if (isExternalLike(trimmed)) return trimmed;
  if (trimmed.startsWith("/")) return trimmed;
  return `/${trimmed.replace(/^\/+/, "")}`;
}
function buildHref(groupName, item) {
  if (isNonEmptyString(item?.link)) return item.link;
  const g = slug(groupName);
  const l = slug(item?.label || "");
  return g && l ? `/category/${g}/${l}` : "#";
}
function chunkItems(list, size) {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

export default function NavAdmin() {
  const [brand, setBrand] = useState("");
  const [brandColor, setBrandColor] = useState("#111111");
  const [brandFontSize, setBrandFontSize] = useState(16);

  const [linkColor, setLinkColor] = useState("#111111");
  const [linkFontSize, setLinkFontSize] = useState(14);

  const [logoFilename, setLogoFilename] = useState("");
  const [logoHeight, setLogoHeight] = useState(40);
  const [uploading, setUploading] = useState(false);

  const [dropdowns, setDropdowns] = useState([emptyDropdown()]);
  const [buttons, setButtons] = useState([
    { name: "suscribe", link: "" },
    { name: "clickhere", link: "" },
  ]);

  const [pages, setPages] = useState([emptyPage()]);
  const [homepageSections, setHomepageSections] = useState([emptyHomepageSection()]); // NEW: Homepage sections
  const [backgroundcolor, setBackgroundcolor] = useState("#ffffff");

  // Alignments
  const [menuAlign, setMenuAlign] = useState("left");
  const [dropdownAlign, setDropdownAlign] = useState("left");
  const [dropdownItemAlign, setDropdownItemAlign] = useState("left");

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [previewMessage, setPreviewMessage] = useState("");

  // Load /navconfig
  useEffect(() => {
    (async () => {
      try {
        const snap = await get(ref(db, "navconfig"));
        if (snap.exists()) {
          const cfg = snap.val();

          setBrand(cfg.brand || cfg.brandname || "");
          setBrandColor(cfg.brandColor || "#111111");
          const bfs = Number(cfg.brandFontSize);
          setBrandFontSize(!Number.isNaN(bfs) && bfs > 0 ? bfs : 16);

          setLinkColor(cfg.linkColor || "#111111");
          const lfs = Number(cfg.linkFontSize);
          setLinkFontSize(!Number.isNaN(lfs) && lfs > 0 ? lfs : 14);

          setLogoFilename(cfg.logo || "");
          setLogoHeight(
            typeof cfg.logoHeight === "number" && !Number.isNaN(cfg.logoHeight)
              ? cfg.logoHeight
              : 40
          );

          setDropdowns(
            Array.isArray(cfg.dropdowns) && cfg.dropdowns.length
              ? cfg.dropdowns
              : [emptyDropdown()]
          );
          setButtons(
            Array.isArray(cfg.buttons) ? cfg.buttons : [{ name: "", link: "" }]
          );

          // Load pages and homepage sections separately
          const allNavItems = Array.isArray(cfg.pages) ? cfg.pages : [];
          
          // Separate regular pages from homepage sections
          const regularPages = allNavItems.filter(item => !item.type || item.type === "page");
          const homepageSectionsList = allNavItems.filter(item => item.type === "homepage");
          
          setPages(
            regularPages.length
              ? regularPages.map((p) => ({
                  label: p?.label || "",
                  href: p?.href || "",
                  show: typeof p?.show === "boolean" ? p.show : true,
                  type: "page",
                }))
              : [emptyPage()]
          );

          setHomepageSections(
            homepageSectionsList.length
              ? homepageSectionsList.map((p) => ({
                  label: p?.label || "",
                  sectionId: p?.sectionId || "",
                  show: typeof p?.show === "boolean" ? p.show : true,
                  type: "homepage",
                }))
              : [emptyHomepageSection()]
          );

          setBackgroundcolor(cfg.backgroundcolor || "#ffffff");

          const ma = (cfg.menuAlign || "left").toLowerCase();
          setMenuAlign(ma === "center" || ma === "right" ? ma : "left");

          const da = (cfg.dropdownAlign || "left").toLowerCase();
          setDropdownAlign(da === "center" || da === "right" ? da : "left");

          const dia = (cfg.dropdownItemAlign || "left").toLowerCase();
          setDropdownItemAlign(
            dia === "center" || dia === "right" ? dia : "left"
          );
        }
      } catch (e) {
        setErr("Failed to load nav config");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const uploadLogo = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      setErr("");
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(UPLOAD_URL, { method: "POST", body: form });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const data = await res.json();
      if (!data?.filename) throw new Error("No filename returned");
      setLogoFilename(data.filename);
    } catch (e) {
      setErr(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // dropdown ops
  const addDropdown = () => setDropdowns((d) => [...d, emptyDropdown()]);
  const removeDropdown = (idx) =>
    setDropdowns((d) => d.filter((_, i) => i !== idx));
  const updateDropdownName = (idx, val) =>
    setDropdowns((d) => d.map((g, i) => (i === idx ? { ...g, name: val } : g)));
  const addItem = (gidx) =>
    setDropdowns((d) =>
      d.map((g, i) =>
        i === gidx
          ? { ...g, items: [...g.items, { id: g.items.length + 1, label: "" }] }
          : g
      )
    );
  const updateItem = (gidx, iidx, field, val) =>
    setDropdowns((d) =>
      d.map((g, i) =>
        i === gidx
          ? {
              ...g,
              items: g.items.map((it, j) =>
                j === iidx
                  ? { ...it, [field]: field === "id" ? Number(val) : val }
                  : it
              ),
            }
          : g
      )
    );
  const removeItem = (gidx, iidx) =>
    setDropdowns((d) =>
      d.map((g, i) =>
        i === gidx ? { ...g, items: g.items.filter((_, j) => j !== iidx) } : g
      )
    );

  // buttons
  const addButton = () =>
    setButtons((b) => [...b, { name: "", link: "" }]);
  const updateButton = (idx, field, val) =>
    setButtons((b) =>
      b.map((btn, i) => (i === idx ? { ...btn, [field]: val } : btn))
    );
  const removeButton = (idx) =>
    setButtons((b) => b.filter((_, i) => i !== idx));

  // pages
  const addPage = () => setPages((p) => [...p, emptyPage()]);
  const updatePage = (idx, field, val) =>
    setPages((p) =>
      p.map((pg, i) =>
        i === idx
          ? { ...pg, [field]: field === "show" ? Boolean(val) : val }
          : pg
      )
    );
  const removePage = (idx) => setPages((p) => p.filter((_, i) => i !== idx));

  // NEW: homepage sections
  const addHomepageSection = () => setHomepageSections((h) => [...h, emptyHomepageSection()]);
  const updateHomepageSection = (idx, field, val) =>
    setHomepageSections((h) =>
      h.map((section, i) =>
        i === idx
          ? { ...section, [field]: field === "show" ? Boolean(val) : val }
          : section
      )
    );
  const removeHomepageSection = (idx) => setHomepageSections((h) => h.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const normalizedPages = pages.map((p) => ({
        ...p,
        href: normalizePageHref(p.href),
        type: "page",
      }));

      // Combine pages and homepage sections into a single array
      const allNavItems = [
        ...normalizedPages,
        ...homepageSections.map(h => ({ ...h, type: "homepage" }))
      ];
      
      await set(ref(db, "navconfig"), {
        brand,
        brandColor,
        brandFontSize,
        linkColor,
        linkFontSize,
        logo: logoFilename,
        logoHeight,
        dropdowns,
        buttons,
        pages: allNavItems, // Save combined array
        backgroundcolor,
        menuAlign,
        dropdownAlign,
        dropdownItemAlign,
      });
      setPages(normalizedPages);
    } catch (e) {
      setErr(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="adminnav-loading">Loading…</div>;

  // ---------- PREVIEW ----------
  const previewLogoSrc = toImageSrc(logoFilename);
  const PREVIEW_MENU = (Array.isArray(dropdowns) ? dropdowns : [])
    .filter((g) => isNonEmptyString(g?.name))
    .map((g) => ({
      label: g.name.trim(),
      items: (Array.isArray(g.items) ? g.items : [])
        .filter((it) => isNonEmptyString(it?.label))
        .map((it) => ({
          label: it.label.trim(),
          href: buildHref(g.name, it),
        })),
    }))
    .filter((g) => g.items.length > 0);

  // Combine regular pages and homepage sections for preview
  const PREVIEW_PAGES = (Array.isArray(pages) ? pages : []).filter(
    (p) => p?.show && isNonEmptyString(p?.label) && isNonEmptyString(p?.href)
  );
  
  const PREVIEW_HOMEPAGE_SECTIONS = (Array.isArray(homepageSections) ? homepageSections : []).filter(
    (h) => h?.show && isNonEmptyString(h?.label) && isNonEmptyString(h?.sectionId)
  );

  const MENU_ALIGN_CLASS =
    menuAlign === "center"
      ? "menu-align-center"
      : menuAlign === "right"
      ? "menu-align-right"
      : "menu-align-left";
  const DROPDOWN_ALIGN_CLASS =
    dropdownAlign === "center"
      ? "align-center"
      : dropdownAlign === "right"
      ? "align-right"
      : "align-left";
  const ITEMS_ALIGN_CLASS =
    dropdownItemAlign === "center"
      ? "items-center"
      : dropdownItemAlign === "right"
      ? "items-right"
      : "items-left";

  const MAX_PER_COL = 7;
  const linkStyle = { color: linkColor, fontSize: `${linkFontSize}px` };

  return (
    <section className="adminnav-wrap">
      <h2 className="adminnav-title">Nav configuration</h2>

      {/* Preview */}
      <div className="adminnav-card">
        <h3 className="adminnav-subtitle">Preview</h3>
        <div
          className="nav-wrapper is-preview"
          style={{
            background: backgroundcolor || "transparent",
            borderRadius: 8,
            overflow: "hidden",
            boxShadow: "0 4px 18px rgba(0,0,0,0.08)",
          }}
        >
          <div className="nav-container">
            <a href="/" className="brand" onClick={(e) => e.preventDefault()}>
              {isNonEmptyString(previewLogoSrc) && (
                <img
                  src={previewLogoSrc}
                  alt="Logo"
                  className="brand-logo"
                  style={{ height: `${logoHeight}px`, width: "auto" }}
                />
              )}
              {isNonEmptyString(brand) && (
                <span
                  className="brand-text"
                  style={{
                    color: brandColor,
                    fontSize: `${brandFontSize}px`,
                  }}
                >
                  {brand}
                </span>
              )}
            </a>

            <nav className="nav-desktop">
              <ul className={`menu ${MENU_ALIGN_CLASS}`}>
                {/* regular page links */}
                {PREVIEW_PAGES.map((p) => (
                  <li key={`page-${p.label}`} className="menu-item">
                    <a
                      href={p.href}
                      className="menu-link"
                      style={linkStyle}
                      onClick={(e) => e.preventDefault()}
                    >
                      {p.label}
                    </a>
                  </li>
                ))}

                {/* homepage section links */}
                {PREVIEW_HOMEPAGE_SECTIONS.map((h) => (
                  <li key={`homepage-${h.label}`} className="menu-item">
                    <a
                      href={`#${h.sectionId}`}
                      className="menu-link homepage-section-link"
                      style={linkStyle}
                      onClick={(e) => {
                        e.preventDefault();
                        setPreviewMessage(
                          `Preview: clicking "${h.label}" scrolls to #${h.sectionId} on Home page.`
                        );
                      }}
                    >
                      {h.label}
                      <span style={{
                        fontSize: "10px",
                        marginLeft: "5px",
                        color: "#666",
                        backgroundColor: "#f0f0f0",
                        padding: "1px 4px",
                        borderRadius: "3px",
                      }}>↓</span>
                    </a>
                  </li>
                ))}

                {/* dropdowns */}
                {PREVIEW_MENU.map((m) => {
                  const columns = chunkItems(m.items, MAX_PER_COL);
                  return (
                    <li key={m.label} className="menu-item has-dropdown">
                      <button
                        className="menu-button"
                        type="button"
                        style={linkStyle}
                      >
                        {m.label}
                        <img
                          src="/chevron-down.svg"
                          alt=""
                          className="chevron"
                          width="14"
                          height="14"
                        />
                      </button>
                      <div
                        className={`dropdown ${DROPDOWN_ALIGN_CLASS} ${ITEMS_ALIGN_CLASS}`}
                        style={{ pointerEvents: "none" }}
                      >
                        <div className="dropdown-columns">
                          {columns.map((col, cIdx) => (
                            <div className="dropdown-col" key={cIdx}>
                              {col.map((it) => (
                                <a
                                  key={it.label}
                                  href={it.href}
                                  className="dropdown-link"
                                  style={linkStyle}
                                  onClick={(e) => e.preventDefault()}
                                >
                                  {it.label}
                                </a>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="right-actions">
              {buttons
                .filter(
                  (b) =>
                    isNonEmptyString(b?.name) && isNonEmptyString(b?.link)
                )
                .map((b, i) => (
                  <a
                    key={`${b.name}-${i}`}
                    href={b.link}
                    className="cta"
                    onClick={(e) => e.preventDefault()}
                  >
                    {b.name}
                </a>
                ))}
            </div>
          </div>
        </div>
        <p className="adminnav-hint" style={{ marginTop: 8 }}>
          This preview uses your current form values and updates instantly. 
          Homepage sections (with ↓ icon) will scroll to that section on the homepage. 
          Click "Save navconfig" to publish changes.
        </p>
        {previewMessage ? (
          <p className="adminnav-hint" style={{ marginTop: 4 }}>
            {previewMessage}
          </p>
        ) : null}
      </div>

      {/* Brand */}
      <div className="adminnav-card">
        <h3 className="adminnav-subtitle">Brand</h3>
        <div className="adminnav-row">
          <div className="adminnav-col">
            <label className="adminnav-label">Brand name (wordmark)</label>
            <input
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              placeholder="e.g., Interior, Weelocal, etc."
            />
            <p className="adminnav-hint">
              This text shows next to the logo in the navbar.
            </p>
          </div>

          <div className="adminnav-col">
            <label className="adminnav-label">Brand color</label>
            <input
              type="color"
              value={brandColor}
              onChange={(e) => setBrandColor(e.target.value)}
            />
            <input
              value={brandColor}
              onChange={(e) => setBrandColor(e.target.value)}
            />
            <p className="adminnav-hint">Hex or CSS color (e.g., #111111).</p>
          </div>

          <div className="adminnav-col" style={{ minWidth: 220 }}>
            <label className="adminnav-label">Brand font size (px)</label>
            <input
              type="range"
              min={10}
              max={64}
              step={1}
              value={brandFontSize}
              onChange={(e) => setBrandFontSize(Number(e.target.value))}
            />
            <input
              type="number"
              min={8}
              max={96}
              value={brandFontSize}
              onChange={(e) => setBrandFontSize(Number(e.target.value || 0))}
            />
            <p className="adminnav-hint">
              Applies to the wordmark text (not the logo image).
            </p>
          </div>
        </div>
      </div>

      {/* Link Typography */}
      <div className="adminnav-card">
        <h3 className="adminnav-subtitle">Link text (pages & dropdown)</h3>
        <div className="adminnav-row">
          <div className="adminnav-col">
            <label className="adminnav-label">Link text color</label>
            <input
              type="color"
              value={linkColor}
              onChange={(e) => setLinkColor(e.target.value)}
            />
            <input
              value={linkColor}
              onChange={(e) => setLinkColor(e.target.value)}
            />
            <p className="adminnav-hint">
              Applies to top nav links and dropdown items.
            </p>
          </div>

          <div className="adminnav-col" style={{ minWidth: 220 }}>
            <label className="adminnav-label">Link font size (px)</label>
            <input
              type="range"
              min={10}
              max={32}
              step={1}
              value={linkFontSize}
              onChange={(e) => setLinkFontSize(Number(e.target.value))}
            />
            <input
              type="number"
              min={8}
              max={48}
              value={linkFontSize}
              onChange={(e) => setLinkFontSize(Number(e.target.value || 0))}
            />
            <p className="adminnav-hint">
              Affects page links and dropdown option text.
            </p>
          </div>
        </div>
      </div>

      {/* Logo */}
      <div className="adminnav-card">
        <h3 className="adminnav-subtitle">Logo</h3>
        <div className="adminnav-row">
          <div className="adminnav-col">
            <label className="adminnav-label">Upload (PNG/JPG)</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => uploadLogo(e.target.files?.[0])}
              disabled={uploading}
            />
            {uploading && <p className="adminnav-note">Uploading…</p>}
            <p className="adminnav-hint">
              POST <code>/upload</code> with form-data key <code>file</code>. We
              store only the returned <code>filename</code>.
            </p>
          </div>
          <div className="adminnav-col">
            <label className="adminnav-label">Saved filename</label>
            <input
              value={logoFilename}
              onChange={(e) => setLogoFilename(e.target.value)}
              placeholder="uuid.png"
            />
          </div>
        </div>

        <div className="adminnav-row" style={{ alignItems: "center" }}>
          <div className="adminnav-col">
            <label className="adminnav-label">Logo height (px)</label>
            <input
              type="range"
              min={24}
              max={120}
              step={1}
              value={logoHeight}
              onChange={(e) => setLogoHeight(Number(e.target.value))}
            />
          </div>
          <div className="adminnav-col" style={{ maxWidth: 120 }}>
            <label className="adminnav-label">Exact px</label>
            <input
              type="number"
              min={16}
              max={200}
              value={logoHeight}
              onChange={(e) => setLogoHeight(Number(e.target.value || 0))}
            />
          </div>
        </div>
      </div>

      {/* Appearance (bg + alignment) */}
      <div className="adminnav-card">
        <h3 className="adminnav-subtitle">Appearance</h3>
        <div className="adminnav-row">
          <div className="adminnav-col">
            <label className="adminnav-label">Background color</label>
            <div className="adminnav-color-picker">
              <input
                type="color"
                value={backgroundcolor === "transparent" ? "#ffffff" : backgroundcolor}
                onChange={(e) => setBackgroundcolor(e.target.value)}
              />
              <input
                value={backgroundcolor}
                onChange={(e) => setBackgroundcolor(e.target.value)}
                placeholder="#ffffff or transparent"
              />
            </div>
            <p className="adminnav-hint">
              Use "transparent", "none", or any CSS color value (hex, rgba, etc.)
            </p>
          </div>

          <div className="adminnav-col">
            <label className="adminnav-label">Nav buttons alignment</label>
            <div className="adminnav-radio-group">
              <label>
                <input
                  type="radio"
                  name="menu-align"
                  value="left"
                  checked={menuAlign === "left"}
                  onChange={(e) => setMenuAlign(e.target.value)}
                />{" "}
                Left
              </label>
              <label>
                <input
                  type="radio"
                  name="menu-align"
                  value="center"
                  checked={menuAlign === "center"}
                  onChange={(e) => setMenuAlign(e.target.value)}
                />{" "}
                Center
              </label>
              <label>
                <input
                  type="radio"
                  name="menu-align"
                  value="right"
                  checked={menuAlign === "right"}
                  onChange={(e) => setMenuAlign(e.target.value)}
                />{" "}
                Right
              </label>
            </div>
            <p className="adminnav-hint">
              Positions the nav buttons between the brand and right actions.
            </p>
          </div>
        </div>

        <div className="adminnav-row">
          <div className="adminnav-col">
            <label className="adminnav-label">Dropdown panel anchor</label>
            <div className="adminnav-radio-group">
              <label>
                <input
                  type="radio"
                  name="dd-align"
                  value="left"
                  checked={dropdownAlign === "left"}
                  onChange={(e) => setDropdownAlign(e.target.value)}
                />{" "}
                Left
              </label>
              <label>
                <input
                  type="radio"
                  name="dd-align"
                  value="center"
                  checked={dropdownAlign === "center"}
                  onChange={(e) => setDropdownAlign(e.target.value)}
                />{" "}
                Center
              </label>
              <label>
                <input
                  type="radio"
                  name="dd-align"
                  value="right"
                  checked={dropdownAlign === "right"}
                  onChange={(e) => setDropdownAlign(e.target.value)}
                />{" "}
                Right
              </label>
            </div>
            <p className="adminnav-hint">
              Where the dropdown panel is anchored relative to its button.
            </p>
          </div>

          <div className="adminnav-col">
            <label className="adminnav-label">Dropdown items alignment</label>
            <div className="adminnav-radio-group">
              <label>
                <input
                  type="radio"
                  name="dd-items-align"
                  value="left"
                  checked={dropdownItemAlign === "left"}
                  onChange={(e) => setDropdownItemAlign(e.target.value)}
                />{" "}
                Left
              </label>
              <label>
                <input
                  type="radio"
                  name="dd-items-align"
                  value="center"
                  checked={dropdownItemAlign === "center"}
                  onChange={(e) => setDropdownItemAlign(e.target.value)}
                />{" "}
                Center
              </label>
              <label>
                <input
                  type="radio"
                  name="dd-items-align"
                  value="right"
                  checked={dropdownItemAlign === "right"}
                  onChange={(e) => setDropdownItemAlign(e.target.value)}
                />{" "}
                Right
              </label>
            </div>
            <p className="adminnav-hint">
              Text/links alignment inside each dropdown column.
            </p>
          </div>
        </div>
      </div>

      {/* Pages */}
      <div className="adminnav-card">
        <div className="adminnav-headrow">
          <h3 className="adminnav-subtitle">Pages (simple nav links)</h3>
          <button className="adminnav-btn ghost" onClick={addPage}>
            + Add page
          </button>
        </div>

        {pages.map((p, i) => (
          <div className="adminnav-row" key={`pg-${i}`}>
            <div className="adminnav-col">
              <label className="adminnav-label">Label</label>
              <input
                value={p.label}
                onChange={(e) => updatePage(i, "label", e.target.value)}
                placeholder="About us"
              />
            </div>
            <div className="adminnav-col">
              <label className="adminnav-label">Link (href)</label>
              <input
                value={p.href}
                onChange={(e) => updatePage(i, "href", e.target.value)}
                placeholder="/about"
              />
            </div>
            <div className="adminnav-col" style={{ alignItems: "flex-start" }}>
              <label className="adminnav-label">Show</label>
              <input
                type="checkbox"
                checked={!!p.show}
                onChange={(e) => updatePage(i, "show", e.target.checked)}
                style={{ width: 18, height: 18 }}
              />
            </div>
            <div className="adminnav-col" style={{ alignItems: "flex-end" }}>
              <button
                className="adminnav-btn danger"
                onClick={() => removePage(i)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        <p className="adminnav-hint">
          Use this for simple top-level links like "About us", "Contact us", etc.
        </p>
      </div>

      {/* NEW: Homepage Sections */}
      <div className="adminnav-card">
        <div className="adminnav-headrow">
          <h3 className="adminnav-subtitle">Homepage Sections (Anchor Links)</h3>
          <button className="adminnav-btn ghost" onClick={addHomepageSection}>
            + Add homepage section
          </button>
        </div>
        <p className="adminnav-hint" style={{ marginBottom: "15px" }}>
          These links will scroll to specific sections on the homepage. They appear in the navigation with a down arrow icon.
        </p>

        {homepageSections.map((h, i) => (
          <div className="adminnav-row" key={`homepage-${i}`}>
            <div className="adminnav-col">
              <label className="adminnav-label">Label (Display Text)</label>
              <input
                value={h.label}
                onChange={(e) => updateHomepageSection(i, "label", e.target.value)}
                placeholder="e.g., Our Services"
              />
            </div>
            <div className="adminnav-col">
              <label className="adminnav-label">Homepage Section</label>
              <select
                value={h.sectionId}
                onChange={(e) => updateHomepageSection(i, "sectionId", e.target.value)}
              >
                <option value="">-- Select a section --</option>
                {HOMEPAGE_SECTIONS.map(section => (
                  <option key={section.id} value={section.id}>
                    {section.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="adminnav-col" style={{ alignItems: "flex-start" }}>
              <label className="adminnav-label">Show</label>
              <input
                type="checkbox"
                checked={!!h.show}
                onChange={(e) => updateHomepageSection(i, "show", e.target.checked)}
                style={{ width: 18, height: 18 }}
              />
            </div>
            <div className="adminnav-col" style={{ alignItems: "flex-end" }}>
              <button
                className="adminnav-btn danger"
                onClick={() => removeHomepageSection(i)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        <div className="adminnav-hint" style={{ marginTop: "10px", padding: "10px", backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
          <strong>Note:</strong> The following sections are available on the homepage:
          <ul style={{ margin: "5px 0 0 15px", fontSize: "12px" }}>
            {HOMEPAGE_SECTIONS.map(section => (
              <li key={section.id}>
                <code>#{section.id}</code> - {section.label}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Dropdowns */}
      <div className="adminnav-card">
        <div className="adminnav-headrow">
          <h3 className="adminnav-subtitle">Dropdowns</h3>
          <button className="adminnav-btn ghost" onClick={addDropdown}>
            + Add dropdown
          </button>
        </div>

        {dropdowns.map((g, gi) => (
          <div className="adminnav-group" key={gi}>
            <div className="adminnav-row">
              <div className="adminnav-col">
                <label className="adminnav-label">Name</label>
                <input
                  value={g.name}
                  onChange={(e) => updateDropdownName(gi, e.target.value)}
                  placeholder="e.g., Design Ideas"
                />
              </div>
              <div className="adminnav-col" style={{ alignItems: "flex-end" }}>
                <button
                  className="adminnav-btn danger"
                  onClick={() => removeDropdown(gi)}
                >
                  Remove
                </button>
              </div>
            </div>

            <div className="adminnav-items">
              <div className="adminnav-items-head">
                <span>#</span>
                <span>Label</span>
                <span />
              </div>
              {g.items.map((it, ii) => (
                <div className="adminnav-item" key={ii}>
                  <input
                    className="adminnav-id"
                    type="number"
                    value={it.id}
                    min={1}
                    onChange={(e) => updateItem(gi, ii, "id", e.target.value)}
                  />
                  <input
                    className="adminnav-labelin"
                    value={it.label}
                    onChange={(e) =>
                      updateItem(gi, ii, "label", e.target.value)
                    }
                    placeholder="e.g., Living Room"
                  />
                  <button
                    className="adminnav-btn danger"
                    onClick={() => removeItem(gi, ii)}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                className="adminnav-btn ghost"
                onClick={() => addItem(gi)}
              >
                + Add item
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Buttons */}
      <div className="adminnav-card">
        <div className="adminnav-headrow">
          <h3 className="adminnav-subtitle">Buttons</h3>
          <button className="adminnav-btn ghost" onClick={addButton}>
            + Add button
          </button>
        </div>

        {buttons.map((b, i) => (
          <div className="adminnav-row" key={i}>
            <div className="adminnav-col">
              <label className="adminnav-label">Name</label>
              <input
                value={b.name}
                onChange={(e) => updateButton(i, "name", e.target.value)}
                placeholder="e.g., suscribe"
              />
            </div>
            <div className="adminnav-col">
              <label className="adminnav-label">Link</label>
              <input
                value={b.link}
                onChange={(e) => updateButton(i, "link", e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div className="adminnav-col" style={{ alignItems: "flex-end" }}>
              <button
                className="adminnav-btn danger"
                onClick={() => removeButton(i)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      {err && <p className="adminnav-error">{err}</p>}
      <div className="adminnav-actions">
        <button
          className="adminnav-btn primary"
          onClick={save}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save navconfig"}
        </button>
      </div>
    </section>
  );
}
